import { Matrix4, Quaternion, Vector3 } from '@technology/web3d';
import {
  AlignmentNavigationCapability,
  FrustumCapability,
  ImageCapabilityBase,
  MapNavigationCapability,
  PlanarCapability,
  StationsNavigationCapability,
  findClosestStation,
} from '@trimble/web3d-capabilities';
import { StationsCapabilityProvider } from '@trimble/web3d-providers';
import type { PositioningData } from '@trimble/web3d-providers';
import { arePointsEqual, partialPointToVector3 } from '@trimble/utilities';
import { CapabilityRegistryContainer } from '@trimble/web3d';
import { getActiveNavCap, isPointVisibleInMap } from '@trimble/pane-sync';

const FREE_VIEW_OFFSET_DISTANCE = 10;

/**
 * Applies PositioningData from a saved view to a target pane by routing
 * through the appropriate capability-specific logic.
 */
export async function applyPositioningData(
  targetPane: CapabilityRegistryContainer,
  data: PositioningData
): Promise<void> {
  const targetViewer = targetPane.viewerWrapper()?.viewer;
  if (!targetViewer) return;

  const navCap = getActiveNavCap(targetPane);

  if (navCap instanceof ImageCapabilityBase) {
    applyToImageCapability(navCap, targetPane, targetViewer, data);
  } else if (navCap instanceof StationsNavigationCapability) {
    applyToStationsCapability(navCap, targetPane, targetViewer, data);
  } else if (navCap instanceof AlignmentNavigationCapability) {
    await applyToAlignmentCapability(navCap, data);
  } else if (navCap instanceof MapNavigationCapability) {
    applyToMapCapability(targetViewer, data);
  } else {
    applyToFreeViewCapability(targetViewer, data);
  }

  const frustumCap = targetPane.get(FrustumCapability)();
  frustumCap?.setFrustumPosition({
    targetPosition: data.targetPosition,
    quaternion: data.quaternion,
    fov: data.fov,
  });
}

function applyToImageCapability(
  navCap: ImageCapabilityBase,
  targetPane: CapabilityRegistryContainer,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Viewer type from @technology/web3d
  targetViewer: any,
  data: PositioningData
): void {
  if (!data.targetPosition) return;

  const stationsProvider = targetPane.get(StationsCapabilityProvider)();
  if (!stationsProvider) return;

  const closestStationInfo = findClosestStation(
    partialPointToVector3(data.targetPosition),
    stationsProvider.stationsInfo().map((s) => s.position)
  );

  if (
    closestStationInfo &&
    closestStationInfo.index !== navCap.selectedStationIndex()
  ) {
    navCap.goToStation(closestStationInfo.index);
  }

  // PlanarCapability manages its own camera orientation via PlanarControls
  if (!(navCap instanceof PlanarCapability)) {
    if (data.quaternion) {
      navCap.applyCameraOrientation(
        new Quaternion(...data.quaternion),
        data.fov ?? undefined
      );
    } else if (data.fov != null) {
      targetViewer.camera.fieldOfView = data.fov;
    }
  }
}

function applyToStationsCapability(
  navCap: StationsNavigationCapability,
  targetPane: CapabilityRegistryContainer,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Viewer type from @technology/web3d
  targetViewer: any,
  data: PositioningData
): void {
  if (!navCap.isActive()) return;

  if (data.targetPosition) {
    const stationsProvider = targetPane.get(StationsCapabilityProvider)();
    if (stationsProvider) {
      const closestStationInfo = findClosestStation(
        partialPointToVector3(data.targetPosition),
        stationsProvider.stationsInfo().map((s) => s.position)
      );
      if (
        closestStationInfo &&
        !arePointsEqual(
          targetViewer.camera.position,
          closestStationInfo.position
        )
      ) {
        navCap.goToStation(closestStationInfo.index);
      }
    }
  }

  if (data.quaternion) {
    navCap.applyCameraOrientation(
      new Quaternion(...data.quaternion),
      data.fov ?? undefined
    );
  } else if (data.fov != null) {
    targetViewer.camera.fieldOfView = data.fov;
  }
}

async function applyToAlignmentCapability(
  navCap: AlignmentNavigationCapability,
  data: PositioningData
): Promise<void> {
  if (!data.targetPosition) return;

  const target = new Vector3(
    data.targetPosition.x,
    data.targetPosition.y,
    'z' in data.targetPosition ? (data.targetPosition.z ?? 0) : 0
  );

  if (!navCap.crossSectionEnabled() && data.quaternion) {
    await navCap.navigateToWorldPosition(
      target,
      new Quaternion(...data.quaternion)
    );
  } else {
    await navCap.navigateToWorldPosition(target);
  }
}

const DEFAULT_CAMERA_HEIGHT = 2000;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Web3DViewer camera types not fully exposed
function applyToMapCapability(targetViewer: any, data: PositioningData): void {
  if (!data.targetPosition) return;

  const syncPos = partialPointToVector3(data.targetPosition);

  if (!isPointVisibleInMap(syncPos, targetViewer)) {
    targetViewer.camera.position = new Vector3(
      syncPos.x,
      syncPos.y,
      targetViewer.camera.position.z !== 0
        ? targetViewer.camera.position.z
        : DEFAULT_CAMERA_HEIGHT
    );
  }
}

function applyToFreeViewCapability(
  targetViewer: {
    camera: { position: Vector3; quaternion: Quaternion; fieldOfView: number };
  },
  data: PositioningData
): void {
  if (data.cameraPosition) {
    const newPos = partialPointToVector3(data.cameraPosition);
    if (!targetViewer.camera.position.equals(newPos)) {
      targetViewer.camera.position = newPos;
    }
  }

  if (data.targetPosition) {
    const targetPos = partialPointToVector3(data.targetPosition);

    if (
      data.cameraPosition &&
      arePointsEqual(data.targetPosition, targetViewer.camera.position)
    ) {
      return;
    }

    if (arePointsEqual(targetPos, targetViewer.camera.position)) {
      const offset = new Vector3(0, 0, 1)
        .applyQuaternion(targetViewer.camera.quaternion)
        .multiplyScalar(FREE_VIEW_OFFSET_DISTANCE);
      targetViewer.camera.position = new Vector3(
        targetViewer.camera.position.x + offset.x,
        targetViewer.camera.position.y + offset.y,
        targetViewer.camera.position.z + offset.z
      );
    }

    targetViewer.camera.quaternion = new Quaternion().setFromRotationMatrix(
      new Matrix4().lookAt(
        targetViewer.camera.position,
        targetPos,
        new Vector3(0, 0, 1)
      )
    );
  } else if (data.cameraPosition && data.quaternion) {
    targetViewer.camera.quaternion = new Quaternion(...data.quaternion);
    if (data.fov != null && targetViewer.camera.fieldOfView !== data.fov) {
      targetViewer.camera.fieldOfView = data.fov;
    }
  }
}
